import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import pushNotificationService from '../services/pushNotificationService';
import toast from 'react-hot-toast';

const PushNotificationContext = createContext();

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      const supported = pushNotificationService.isSupported;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        if (user && token) {
          await pushNotificationService.init();
          const subscription = await pushNotificationService.getSubscription();
          setIsSubscribed(!!subscription);
        }
      }
    };

    checkSupport();
  }, [user, token]);

  const subscribeToPushNotifications = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    setLoading(true);
    try {
      await pushNotificationService.subscribe();
      setIsSubscribed(true);
      setPermission('granted');
      toast.success('Push notifications enabled successfully');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      if (error.message.includes('permission denied')) {
        setPermission('denied');
        toast.error('Push notification permission was denied');
      } else {
        toast.error('Failed to enable push notifications');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const unsubscribeFromPushNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const success = await pushNotificationService.unsubscribe();
      if (success) {
        setIsSubscribed(false);
        toast.success('Push notifications disabled');
      } else {
        toast.error('Failed to disable push notifications');
      }
      return success;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      toast.error('Failed to disable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const showTestNotification = useCallback(async () => {
    if (!isSubscribed) {
      toast.error('Please enable push notifications first');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/push/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Test notification sent');
      } else {
        toast.error('Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  }, [isSubscribed, token]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    showTestNotification
  }), [
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    showTestNotification
  ]);

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};